import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {Box, HStack} from '@chakra-ui/react'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import ProductThumbnail from '../product-thumbnail'
import {FormattedMessage} from 'react-intl'
import {selectablesValuesFiltered} from '../../custom-hooks/use-custom-variation-group'
import {remCalc} from '../../theme/_utils'
import {
    PRODUCT_CUSTOM_EXCLUDE_ATTRIBUTE_ID,
    PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID,
} from '../../constants'

function ProductVariants({variationAttributes, product}) {
    return variationAttributes.map(({id, name, selectedValue, values}) => {
        if (id === PRODUCT_CUSTOM_EXCLUDE_ATTRIBUTE_ID) return

        if (id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID)
            values = selectablesValuesFiltered(values, product?.c_enabledVariationGroups ?? [])

        const firstElement = values.find((val) => val.value === selectedValue.value)
        const remainingElements = values.filter((val) => val.value !== selectedValue.value)

        const sortedValues =
            id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID && firstElement && remainingElements
                ? [firstElement, ...remainingElements]
                : values

        const swatches = sortedValues.map((props, index) => {
            let {href, name, image, value, orderable} = props

            const search = href.split('?')?.[1]
            let pid = null
            if (search) {
                const params = new URLSearchParams(search)
                pid = params.get('pid')
            }

            const productUrl = product?.c_enabledVariationGroups?.find(
                (item) => item.productId === value,
            )
            if (productUrl) {
                href = productUrl?.slugUrl
                if (pid) {
                    href += '?pid=' + pid
                }
            }

            const hasSelection = Boolean(selectedValue?.value)
            const isSelected = selectedValue?.value === value
            let content

            if (image) {
                const heroImageGroup = findImageGroupBy(product.imageGroups, {
                    viewType: 'small',
                    selectedVariationAttributes: {[PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID]: value},
                })

                const heroImage = heroImageGroup?.images?.[0]
                content = (
                    <ProductThumbnail
                        handleSelection={() => {}}
                        selected={isSelected}
                        image={heroImage}
                        variant="colorThumbnail"
                    />
                )
            } else {
                content = name
            }
            const isFirst = index === 0
            // To mimic the behavior of a native radio input, only
            // one swatch should receive tab focus; the rest can be
            // selected using arrow keys when the swatch group has
            // focus. The focused element is the selected option or
            // the first in the group, if no option is selected.
            // This is a slight difference, for simplicity, from the
            // native element, where the first element is focused on
            // `Tab` and the _last_ element is focused on `Shift+Tab`
            const isFocusable = isSelected || (!hasSelection && isFirst)

            return (
                <Swatch
                    key={value}
                    href={href}
                    disabled={!orderable}
                    value={value}
                    name={name}
                    variant={id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID ? 'image' : 'square'}
                    selected={isSelected}
                    isFocusable={isFocusable}
                    position={index + 1}
                    total={sortedValues?.length}
                >
                    {content}
                </Swatch>
            )
        })

        if (id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID && swatches.length === 1) {
            if (selectedValue?.name?.toLowerCase() === 'undefined')
                return <Fragment key={id}></Fragment>

            return (
                <HStack key={id} color="text.default" fontSize={remCalc(14)}>
                    <Box fontWeight="regular">
                        {id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID ? (
                            <FormattedMessage id="variants.label.color" defaultMessage="Color" />
                        ) : (
                            name
                        )}
                    </Box>
                    <Box fontWeight="bold">{selectedValue?.displayValue || ''}</Box>
                </HStack>
            )
        }

        return (
            <SwatchGroup
                key={id}
                value={selectedValue?.value}
                displayName={selectedValue?.displayValue || ''}
                id={id}
                label={name}
            >
                {swatches}
            </SwatchGroup>
        )
    })
}

ProductVariants.propTypes = {
    variationAttributes: PropTypes.array,
    product: PropTypes.object,
}

export default ProductVariants
